<template>
  <div class="step-container">
    <StepperHeading :title="'Select Solution'" :step="1">
      <template #help>
        <p>
          In this step, you will select a solution for processing your food surplus. Click on a solution to view its details. 
          The chosen solution will be used for further lifecycle analysis.
        </p>
      </template>
    </StepperHeading>

    <!-- Reuse (human) Filter Notice -->
    <div class="filter-notice">
      <div class="notice-icon">
        <i class="bi bi-funnel-fill"></i>
      </div>
      <div class="notice-content">
        <h5 class="notice-title">Important: Filtered Results</h5>
        <p class="notice-text">
          Currently, the environmental impact analysis is only available for solutions in the 
          <strong>"Reuse (human)"</strong> category. Only solutions from this category are shown below.
        </p>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="isLoading" class="loading-container">
      <div class="spinner-container">
        <div class="spinner-border text-success" role="status">
          <span class="visually-hidden">Loading solutions...</span>
        </div>
      </div>
      <p>Loading solutions...</p>
    </div>

    <!-- Solutions List with only headers -->
    <div v-else-if="filteredSolutions && filteredSolutions.length > 0" class="solutions-container">
      <div class="list-header">
        <h5>
          <i class="bi bi-check2-square me-2"></i>
          Available Solutions
        </h5>
        <span class="solutions-count">{{ filteredSolutions.length }} found</span>
      </div>

      <!-- Solution Cards List (only headers) -->
      <div class="solutions-list-container">
        <div class="solutions-list">
          <div 
            v-for="(solution, index) in filteredSolutions" 
            :key="solution.solution_id" 
            class="list-item-card"
            :class="{'selected': isSelected(solution)}"
            @click="selectSolution(solution)"
          >
            <!-- Solution Card Header - Always visible -->
            <div class="list-item-header">
              <div class="item-index">{{ index + 1 }}</div>
              <div class="item-info">
                <h3 class="item-name">{{ getProcessName(solution) }}</h3>
                <div class="item-categories">
                  <span class="category-badge">{{ getSolutionCategory(solution) }}</span>
                  <span v-if="getSolutionSubCategory(solution)" class="subcategory-badge">
                    {{ getSolutionSubCategory(solution) }}
                  </span>
                </div>
              </div>
              <div class="item-actions">
                <button 
                  class="btn select-btn" 
                  v-if="!isSelected(solution)"
                  @click.stop="selectSolution(solution)"
                  title="Select this solution"
                >
                  Select
                </button>
                <span class="selected-indicator" v-else title="Selected solution">
                  <i class="bi bi-check-circle-fill"></i>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Selected Solution Details (shown when a solution is selected) -->
      <div v-if="lcaData.selectedSolution" class="selected-solution-details">
        <div class="detail-card solution-info-card">
          <div class="card-header">
            <h2 class="card-title">
              <i class="bi bi-lightbulb"></i>
              Selected Solution
            </h2>
          </div>
          <div class="card-body">
            <template v-if="isVcgSolution(lcaData.selectedSolution)">
              <VcgSolutionDetails :vcgSolution="lcaData.selectedSolution" />
            </template>
            <template v-else>
              <FoodcycleSolutionDetails :fcSolution="lcaData.selectedSolution" />
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div v-else class="empty-state">
      <div class="empty-state-icon">
        <i class="bi bi-search"></i>
      </div>
      <h3>No Suitable Solutions Found</h3>
      <p>We couldn't find any "Reuse (human)" solutions based on your input. Please try adjusting your criteria or select a different category if available.</p>
      <button class="btn back-to-surplus-btn" @click="goBackToSurplus">
        <i class="bi bi-arrow-left me-2"></i>
        Back to Food Surplus
      </button>
    </div>
  </div>
</template>

<script>
import { useStepperStore } from '@/stores/stepper';
import { usePageContextStore } from '@/stores/pageContext';
import { useLCAStore } from '@/stores/lca';
import StepperHeading from '@/components/Stepper/StepperHeading.vue';
import FoodcycleSolutionDetails from '@/components/solution_details/templates/FoodcycleSolutionDetails.vue';
import VcgSolutionDetails from '@/components/solution_details/templates/VcgSolutionDetails.vue';
import { useRouter } from 'vue-router';
import { onMounted, computed } from 'vue';
import axios from 'axios';

export default {
  name: 'SolutionSelector',
  components: { 
    StepperHeading,
    FoodcycleSolutionDetails,
    VcgSolutionDetails
  },
  setup() {
    const stepperStore = useStepperStore();
    const pageContextStore = usePageContextStore();
    const lcaStore = useLCAStore();
    const router = useRouter();

    // Set page context
    pageContextStore.setPageContext(
      'Select Solution',
      'On this page, select the solution to be used for processing.',
      'Solutions are filtered to show only those in the "Reuse (human)" category, as the environmental impact analysis is currently only available for this type.'
    );

    const isLoading = computed(() => !lcaStore.lcaData.solutions.length);
    const solutions = computed(() => lcaStore.lcaData.solutions);
    
    // Filter solutions to only show "Reuse (human)" category
    const filteredSolutions = computed(() => {
      return solutions.value.filter(solution => {
        const category = solution.solution_category || solution.solutionCategory || '';
        return category.toLowerCase() === 'reuse (human)';
      });
    });

    function selectSolution(solution) {
      if (solution) {
        lcaStore.setSelectedSolution(solution);
      }
    }

    function isSelected(solution) {
      if (!solution) return false;
      return lcaStore.lcaData.selectedSolution?.solution_id === solution.solution_id;
    }
    
    function goBackToSurplus() {
      router.push({ name: 'food-surplus' });
    }

    // Check if solution is VCG
    function isVcgSolution(solution) {
      return solution?.solution_origin === 'vcg.ai';
    }
    
    // Helper functions for consistent display
    function getProcessName(sol) {
      return sol.process_name ?? sol.processName ?? 'â€"';
    }
    function getSolutionCategory(sol) {
      return sol.solution_category ?? sol.solutionCategory ?? 'â€"';
    }
    function getSolutionSubCategory(sol) {
      return sol.solution_subcategory ?? sol.solutionSubcategory ?? 'â€"';
    }

    async function fetchSolutions() {
      try {
        const userInputs = {
          companyProfile: { ...stepperStore.companyProfile },
          wasteInfo: { ...stepperStore.wasteInfo },
        };
        const includeVCG = true;

        const response = await axios.post(
          `/solutions/match?includeVCG=${includeVCG}`,
          { userInputs }
        );
        const data = response.data;
        if (data.solutions) {
          stepperStore.setSolutions(data.solutions);
          lcaStore.setSolutions(data.solutions);
        } else {
          stepperStore.setSolutions([]);
          lcaStore.setSolutions([]);
        }
      } catch (error) {
        console.error('Error matching solutions:', error);
        stepperStore.setSolutions([]);
        lcaStore.setSolutions([]);
      }
    }

    onMounted(async () => {
      if (!stepperStore.solutions.length) {
        await fetchSolutions();
      } else {
        lcaStore.setSolutions(stepperStore.solutions);
      }
    });

    return {
      solutions,
      filteredSolutions,
      lcaData: lcaStore.lcaData,
      selectSolution,
      isSelected,
      isLoading,
      goBackToSurplus,
      getProcessName,
      getSolutionCategory,
      getSolutionSubCategory,
      isVcgSolution,
    };
  },
};
</script>

<style scoped>
.step-container {
  padding: 2rem;
  border-radius: 12px;
  background-color: white;
}

/* Filter Notice */
.filter-notice {
  margin-bottom: 2rem;
  background-color: #fff8e1;
  border: 1px solid #ffecb3;
  border-radius: 10px;
  padding: 1.25rem;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.notice-icon {
  font-size: 1.4rem;
  color: #ffa000;
  flex-shrink: 0;
  margin-top: 0.2rem;
}

.notice-title {
  margin: 0 0 0.5rem 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #f57f17;
}

.notice-text {
  margin: 0;
  color: #5d4037;
  font-size: 0.95rem;
  line-height: 1.5;
}

/* Loading state */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 0;
}

.spinner-container {
  margin-bottom: 1rem;
}

.spinner-border.text-success {
  color: #87b140 !important;
}

/* Solutions container and header */
.solutions-container {
  margin-top: 1rem;
}

.list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #eee;
}

.list-header h5 {
  margin: 0;
  font-weight: 700;
  color: #333;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
}

.solutions-count {
  background-color: rgba(135, 177, 64, 0.15);
  color: #558000;
  font-weight: 600;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.85rem;
}

/* Solutions list container - scrollable */
.solutions-list-container {
  max-height: 500px;
  overflow-y: auto;
  border-radius: 10px;
  border: 1px solid #eee;
  margin-bottom: 1rem;
}

/* Solutions List */
.solutions-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 0.5rem;
}

/* List Item Card - Unified with ProviderSelector */
.list-item-card {
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.06);
  border: 1px solid #e0e0e0;
  transition: all 0.2s ease-in-out;
  cursor: pointer;
  min-height: 70px;
  overflow: hidden;
}

.list-item-card:hover {
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
  border-color: #cccccc;
}

.list-item-card.selected {
  border-color: #87b140;
  box-shadow: 0 0 0 1px #87b140, 0 3px 8px rgba(135, 177, 64, 0.2);
  background-color: #f8fbf3;
}

.list-item-header {
  display: flex;
  align-items: center;
  padding: 0.9rem 1.25rem;
  background-color: transparent;
  transition: background-color 0.2s ease;
  min-height: 68px;
  width: 100%;
  box-sizing: border-box;
}

.item-index {
  width: 30px;
  height: 30px;
  background-color: #f0f0f0;
  color: #555;
  font-weight: 600;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  flex-shrink: 0;
  font-size: 0.9rem;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.list-item-card.selected .item-index {
  background-color: #87b140;
  color: white;
}

.item-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.25rem;
}

.item-name {
  font-size: 1.05rem;
  font-weight: 600;
  color: #333;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.item-categories {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.category-badge {
  display: inline-block;
  background-color: rgba(135, 177, 64, 0.1);
  color: #558000;
  padding: 0.2rem 0.6rem;
  border-radius: 15px;
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.3;
  white-space: nowrap;
}

.subcategory-badge {
  display: inline-block;
  background-color: rgba(0, 0, 0, 0.05);
  color: #555;
  padding: 0.2rem 0.6rem;
  border-radius: 15px;
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.3;
  white-space: nowrap;
}

.item-actions {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  margin-left: 1rem;
}

.select-btn {
  background-color: white;
  color: #87b140;
  border: 1px solid #ccc;
  border-radius: 6px;
  padding: 0.35rem 0.75rem;
  font-weight: 600;
  font-size: 0.85rem;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.select-btn:hover {
  background-color: #f2f5e7;
  border-color: #87b140;
  color: #558000;
}

.selected-indicator {
  color: #87b140;
  font-size: 1.3rem;
  line-height: 1;
}

/* Selected Solution Details */
.selected-solution-details {
  margin-top: 2rem;
}

.detail-card {
  background-color: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(135, 177, 64, 0.1);
  transition: box-shadow 0.3s ease;
  animation: fadeIn 0.5s ease;
}

.card-header {
  padding: 1.5rem;
  background-color: #f2f5e7;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.card-title {
  margin: 0;
  color: #333;
  font-size: 1.4rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.card-title i {
  color: #87b140;
  font-size: 1.2rem;
}

.card-body {
  padding: 1.5rem;
}

/* Empty state styling */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 3rem 2rem;
  margin-top: 1.5rem;
}

.empty-state-icon {
  background-color: #f2f5e7;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: #87b140;
  margin-bottom: 1.5rem;
}

.empty-state h3 {
  color: #333;
  font-weight: 700;
  margin-bottom: 1rem;
}

.empty-state p {
  color: #666;
  max-width: 500px;
  margin-bottom: 1.5rem;
  line-height: 1.6;
}

.back-to-surplus-btn {
  background-color: #f0f0f0;
  color: #444;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
}

.back-to-surplus-btn:hover {
  background-color: #e5e5e5;
  transform: translateY(-2px);
}

/* Animations */
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Responsive adjustments */
@media (max-width: 992px) {
  .step-container {
    padding: 1.5rem;
  }
}

@media (max-width: 768px) {
  .step-container {
    padding: 1.25rem;
  }
  
  .filter-notice {
    flex-direction: column;
    padding: 1rem;
  }
  
  .list-item-header {
    padding: 0.8rem 1rem;
  }
  
  .item-name {
    font-size: 1rem;
  }
  
  .solutions-list-container {
    max-height: 400px;
  }
}

@media (max-width: 576px) {
  .step-container {
    padding: 1rem;
  }
  
  /* Adjust list item header for wrapping */
  .list-item-header {
    flex-wrap: wrap;
    padding: 0.75rem;
    min-height: 0;
  }
  
  .item-index {
    margin-right: 0.75rem;
  }
  
  .item-info {
    width: calc(100% - 40px - 0.75rem);
    margin-bottom: 0.5rem;
    order: 1;
  }
  
  .item-actions {
    width: 100%;
    margin-left: 0;
    justify-content: flex-end;
    margin-top: 0.5rem;
    order: 2;
  }
  
  .category-badge, .subcategory-badge {
    font-size: 0.7rem;
    padding: 0.15rem 0.5rem;
  }
  
  .empty-state-icon {
    width: 60px;
    height: 60px;
    font-size: 1.5rem;
  }
  
  .empty-state h3 {
    font-size: 1.1rem;
  }
  
  .empty-state p {
    font-size: 0.9rem;
  }
  
  .solutions-list-container {
    max-height: 350px;
  }
}
</style>